import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
// ProjectContext.jsx is plain JS, so this import is implicitly typed `any`.
import { useMCPQueryScope, mcpRequestOptions } from "./useMCPQueryScope";
import { mcpQueryKeys } from "../query-keys";
import {
  agentToolsApi,
  type ListMCPConnectionsParams,
  type PaginationMetadata,
} from "../../../services/agent-tools-service";
import { mapConnectionDtoToUi } from "../adapters";
import type { MCPConnection } from "../types";

export { MCP_CONNECTIONS_QUERY_KEY } from "../query-keys";

type UseMCPConnectionsResult = {
  connections: MCPConnection[];
  metadata: PaginationMetadata | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
};

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 20;

export const useMCPConnections = (
  params: ListMCPConnectionsParams = {},
): UseMCPConnectionsResult => {
  const { i18n } = useTranslation();
  const page = params.page ?? DEFAULT_PAGE;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const locale = i18n.resolvedLanguage ?? i18n.language;

  // The list is filtered by the selected projects, so they have to be part of
  // the cache key — otherwise switching projects keeps serving the previous
  // selection's connections until the entry goes stale. An "all" selection
  // sends no project_ids at all and lets the API decide the scope.
  const scope = useMCPQueryScope();
  const { projectIds } = scope;
  const query = useQuery({
    queryKey: mcpQueryKeys.list(scope.key, page, pageSize),
    queryFn: ({ signal }) =>
      agentToolsApi.listMCPConnections({ page, pageSize, projectIds }, mcpRequestOptions(scope, signal)),
    enabled: scope.enabled,
    // Keep the previous page's rows while paginating, but not across a project
    // switch — showing another selection's connections under a new scope would
    // read as if they belonged to it.
    placeholderData: (previous, previousQuery) =>
      JSON.stringify(previousQuery?.queryKey?.[1]) === JSON.stringify(mcpQueryKeys.scope(scope.key)[1]) ? previous : undefined,
  });

  const connections = useMemo(
    () =>
      (query.data?.items ?? []).map((connection) =>
        mapConnectionDtoToUi(connection, locale),
      ),
    [locale, query.data],
  );

  return {
    connections,
    metadata: query.data?.metadata,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
  };
};
